import {
	Flex,
	FormControl,
	FormControlText,
	FormGroup,
	Input,
	InputCheckBox
} from "@itandi/itandi-bb-ui";
import { Tooltip } from "@itandi/itandi-bb-ui/Tooltip";
import React from "react";
import { useFormContext } from "react-hook-form";
import { MaintenanceCompanyCombobox } from "~/components/specifics/vendors/deals/index/search/detail/MaintenanceCompanyCombobox";
import { VendorPartnerCombobox } from "~/components/specifics/vendors/deals/index/search/detail/VendorPartnerCombobox";
import { VendorUserCombobox } from "~/components/specifics/vendors/deals/index/search/detail/VendorUserCombobox";
import { useStatuses } from "~/hooks/vendors/useStatuses";
import type { DealCompanySetting } from "~/models/CompanySetting";
import type { DealSearchCondition } from "~/models/Deal";
import { getWithVendorStatuses } from "~/models/Status";
import { ApplicationTypeCheckBox } from "./detail/ApplicationTypeCheckBox";
import { DealTypeCheckBox } from "./detail/DealTypeCheckBox";
import { SearchConditionDate } from "./detail/SearchConditionDate";
import { StatusCombobox } from "./detail/StatusCombobox";

type Props = {
	dealSearchCondition: DealSearchCondition;
	handleChangeMaintenanceCompanyInput: (inputText: string) => void;
	maintenanceCompanies: DealCompanySetting[] | null;
};

export const DealSearchDetailTab: React.FC<Props> = ({
	dealSearchCondition,
	maintenanceCompanies,
	handleChangeMaintenanceCompanyInput
}: Props) => {
	const { register, setValue } = useFormContext<DealSearchCondition>();
	const { data: tmpStatuses } = useStatuses().index();
	const defaultPrioritySort = dealSearchCondition.prioritySort ?? true;
	const [isPrioritySort, setIsPrioritySort] =
		React.useState(defaultPrioritySort);
	const onChangeCompanyMasterId = React.useCallback(
		async (companyMasterId: number | undefined): Promise<void> => {
			setValue("companyMasterId", companyMasterId);
		},
		[setValue]
	);
	if (tmpStatuses === null) {
		return null;
	}
	const statuses = getWithVendorStatuses(tmpStatuses);
	return (
		<FormGroup variant="vertical">
			<FormControl label="未読を優先表示する">
				<InputCheckBox
					checked={isPrioritySort}
					label="未読を優先表示する"
					onChange={(e): void => {
						setValue("prioritySort", e.target.checked);
						setIsPrioritySort(e.target.checked);
					}}
				/>
			</FormControl>
			<FormControl label="状況(ステータス)">
				<StatusCombobox
					dealSearchCondition={dealSearchCondition}
					statuses={statuses}
				/>
			</FormControl>
			<FormControl label="状況(申請関連)">
				<Flex marginY={8} wrap="wrap">
					<ApplicationTypeCheckBox
						applicationType="cancel"
						dealSearchCondition={dealSearchCondition}
						key="cancel"
						label="取下申請あり"
					/>
					<ApplicationTypeCheckBox
						applicationType="requestEstimation"
						dealSearchCondition={dealSearchCondition}
						key="requestEstimation"
						label="見積申請中"
					/>
					<ApplicationTypeCheckBox
						applicationType="draftEstimation"
						dealSearchCondition={dealSearchCondition}
						key="draftEstimation"
						label="見積編集中"
					/>
					<ApplicationTypeCheckBox
						applicationType="requestEstimationByCompany"
						dealSearchCondition={dealSearchCondition}
						key="requestEstimationByCompany"
						label="見積申請あり"
					/>
					<Flex alignItems="center">
						<Tooltip>
							管理会社により見積りが申請されている案件に絞り込みます
						</Tooltip>
					</Flex>
				</Flex>
			</FormControl>
			<FormControl label="案件タイプ">
				<Flex marginY={8} wrap="wrap">
					<DealTypeCheckBox
						dealSearchCondition={dealSearchCondition}
						dealType="restoration"
						key="restoration"
						label="原状回復"
					/>
					<DealTypeCheckBox
						dealSearchCondition={dealSearchCondition}
						dealType="repair"
						key="repair"
						label="修繕"
					/>
					<DealTypeCheckBox
						dealSearchCondition={dealSearchCondition}
						dealType="other"
						key="other"
						label="その他"
					/>
				</Flex>
			</FormControl>
			<FormControl label="物件名">
				<Input {...register("name")} placeholder="六本木マンション" />
			</FormControl>
			<FormControl label="部屋番号">
				<Input {...register("roomNumber")} placeholder="101" />
			</FormControl>
			<FormControl label="所在地">
				<Input {...register("address")} placeholder="東京都港区六本木1-2-3" />
			</FormControl>
			<FormControl label="入居者">
				<Input {...register("tenantName")} placeholder="内装 太郎" />
			</FormControl>
			<FormControl label="担当者">
				<VendorUserCombobox dealSearchCondition={dealSearchCondition} />
			</FormControl>
			<FormControl label="管理会社">
				<MaintenanceCompanyCombobox
					handleChangeMaintenanceCompanyInput={
						handleChangeMaintenanceCompanyInput
					}
					maintenanceCompanies={maintenanceCompanies}
					onChangeCompanyMasterId={onChangeCompanyMasterId}
					selectedCompanyMasterId={dealSearchCondition.companyMasterId}
				/>
			</FormControl>
			<FormControl label="実施会社">
				<VendorPartnerCombobox dealSearchCondition={dealSearchCondition} />
			</FormControl>
			<FormControl label="更新日">
				<Flex alignItems="center">
					<SearchConditionDate
						attributeName="updatedMinDate"
						defaultValue={dealSearchCondition.updatedMinDate}
					/>
					〜
					<SearchConditionDate
						attributeName="updatedMaxDate"
						defaultValue={dealSearchCondition.updatedMaxDate}
					/>
				</Flex>
			</FormControl>
			<FormControl label="立会日程">
				<Flex alignItems="center">
					<SearchConditionDate
						attributeName="inspectionMinDate"
						defaultValue={dealSearchCondition.inspectionMinDate}
					/>
					〜
					<SearchConditionDate
						attributeName="inspectionMaxDate"
						defaultValue={dealSearchCondition.inspectionMaxDate}
					/>
				</Flex>
			</FormControl>
			<FormControl label="現地調査日程">
				<Flex alignItems="center">
					<SearchConditionDate
						attributeName="surveyMinDate"
						defaultValue={dealSearchCondition.surveyMinDate}
					/>
					〜
					<SearchConditionDate
						attributeName="surveyMaxDate"
						defaultValue={dealSearchCondition.surveyMaxDate}
					/>
				</Flex>
			</FormControl>
			<FormControl label="見積提出日">
				<Flex alignItems="center">
					<SearchConditionDate
						attributeName="sendEstimateMinDate"
						defaultValue={dealSearchCondition.sendEstimateMinDate}
					/>
					〜
					<SearchConditionDate
						attributeName="sendEstimateMaxDate"
						defaultValue={dealSearchCondition.sendEstimateMaxDate}
					/>
				</Flex>
				<FormControlText help="ステータスを「見積確認中」に進めた日" />
			</FormControl>
			<FormControl label="発注日">
				<Flex alignItems="center">
					<SearchConditionDate
						attributeName="orderMinDate"
						defaultValue={dealSearchCondition.orderMinDate}
					/>
					〜
					<SearchConditionDate
						attributeName="orderMaxDate"
						defaultValue={dealSearchCondition.orderMaxDate}
					/>
				</Flex>
				<FormControlText help="ステータスを「工事完了待ち」に進めた日" />
			</FormControl>
			<FormControl label="工期開始日">
				<Flex alignItems="center">
					<SearchConditionDate
						attributeName="constructionMinStartDate"
						defaultValue={dealSearchCondition.constructionMinStartDate}
					/>
					〜
					<SearchConditionDate
						attributeName="constructionMaxStartDate"
						defaultValue={dealSearchCondition.constructionMaxStartDate}
					/>
				</Flex>
			</FormControl>
			<FormControl label="工期終了日">
				<Flex alignItems="center">
					<SearchConditionDate
						attributeName="constructionMinEndDate"
						defaultValue={dealSearchCondition.constructionMinEndDate}
					/>
					〜
					<SearchConditionDate
						attributeName="constructionMaxEndDate"
						defaultValue={dealSearchCondition.constructionMaxEndDate}
					/>
				</Flex>
			</FormControl>
			<FormControl label="検収依頼日">
				<Flex alignItems="center">
					<SearchConditionDate
						attributeName="constructionReportedMinDate"
						defaultValue={dealSearchCondition.constructionReportedMinDate}
					/>
					〜
					<SearchConditionDate
						attributeName="constructionReportedMaxDate"
						defaultValue={dealSearchCondition.constructionReportedMaxDate}
					/>
				</Flex>
				<FormControlText help="ステータスを「検収中」に進めた日" />
			</FormControl>
			<FormControl label="完了日">
				<Flex alignItems="center">
					<SearchConditionDate
						attributeName="completedMinDate"
						defaultValue={dealSearchCondition.completedMinDate}
					/>
					〜
					<SearchConditionDate
						attributeName="completedMaxDate"
						defaultValue={dealSearchCondition.completedMaxDate}
					/>
				</Flex>
			</FormControl>
			<FormControlText help="ステータスを「完了」に進めた日" />
			<FormControl label="登録日">
				<Flex alignItems="center">
					<SearchConditionDate
						attributeName="registrationMinDate"
						defaultValue={dealSearchCondition.registrationMinDate}
					/>
					〜
					<SearchConditionDate
						attributeName="registrationMaxDate"
						defaultValue={dealSearchCondition.registrationMaxDate}
					/>
				</Flex>
			</FormControl>
		</FormGroup>
	);
};
